/**
 * @fileoverview Wrapper around react-intl FormattedMessage component that automatically
 * includes some allowed markup tags such as <strong> or <em>.
 */

import React, { FunctionComponent, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import { RICH_TAGS } from './RichMessageConfig';
import {
  buildRichOutput,
  prepareRichMessage,
  RichValue,
} from './richMessageUtils';

/**
 * Values supported by the rich message renderer are shared in `richMessageUtils`
 * so both the component and hook stay behaviorally aligned.
 */
interface Props {
  id: string;
  defaultMessage: string;
  values?: Record<string, RichValue>;
}

// Wrapper component used to inject common HTML tags in translations
// This allows us to define a kind of "meta language" for translation keys
// with commonly used HTML tags (bold, italic, pre etc.)
export const RichFormattedMessage: FunctionComponent<Props> = ({
  id,
  defaultMessage,
  values,
}: Props) => {
  // Combine default rich tags (e.g. br/strong/em) with call-site values.
  // Call-site values win for duplicate keys.

  // Pull translated text from the current locale catalog first.
  // Fallback to defaultMessage when key is not available.
  const intl = useIntl();
  const translatedFromCatalog = (intl.messages as Record<string, unknown>)?.[
    id
  ];

  // Use translated message if available, otherwise fallback to defaultMessage.
  const baseMessage =
    typeof translatedFromCatalog === 'string'
      ? translatedFromCatalog
      : defaultMessage;

  const prepared = prepareRichMessage({
    baseMessage,
    values,
    sharedValues: RICH_TAGS as Record<string, RichValue>,
  });

  // Fast path: if no rich node placeholders are present, render through the
  // existing WYSIWYG HTML renderer.
  if (!prepared.richKeys.length) {
    return (
      <WYSIWYGText format="full_html" content={prepared.processedMessage} />
    );
  }

  const outputParts: ReactNode[] = buildRichOutput(prepared);

  return <>{outputParts}</>;
};

export default RichFormattedMessage;
